import Phaser from 'phaser';
import { Accessibility, type Level } from '../setup';
import { BALANCE, DEPTHS } from '../config/gameConfig';
import { THEME } from '../config/theme';
import { worldToOverlay, overlayFullScreen } from '../utils/cameraCoords';

/** Size of the headlight glow DynamicTexture (px). Small = cheap blit. */
const HEADLIGHT_DT_SIZE = 256;

export class WeatherSystem {
  private scene: Phaser.Scene;
  private nightOverlay: Phaser.GameObjects.Image | null = null;
  private nightTexKey = '__night_overlay';
  private nightCtx: CanvasRenderingContext2D | null = null;
  private nightDynTex: Phaser.Textures.DynamicTexture | null = null;
  private frostOverlay: Phaser.GameObjects.Image | null = null;
  private frostTexKey = '__frost_vignette';
  private headlightDirection: { x: number; y: number } = { x: 0, y: -1 };
  private weatherParticles: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private windStreaks: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private highContrastMode = false;
  private tileSize: number;
  /** True when using night-colored textures (no full-screen overlay) */
  private useNightTextures = false;

  constructor(scene: Phaser.Scene, tileSize: number) {
    this.scene = scene;
    this.tileSize = tileSize;
  }

  get isHighContrast(): boolean {
    return this.highContrastMode;
  }

  get headlight(): { x: number; y: number } {
    return this.headlightDirection;
  }

  get hasNightOverlay(): boolean {
    return this.nightOverlay !== null;
  }

  createWeatherEffects(level: Level): void {
    if (Accessibility.settings.reducedMotion) return;

    const isStorm = level.weather === 'storm';
    const isLightSnow = level.weather === 'light_snow';

    const config = {
      storm: {
        quantity: 8,
        frequency: 50,
        speedY: { min: 150, max: 300 },
        speedX: { min: -100, max: -30 },
        scale: { start: 0.4, end: 0.1 },
        alpha: { start: 1, end: 0.3 },
        lifespan: 3000,
        tint: THEME.colors.snowfall
      },
      light_snow: {
        quantity: 3,
        frequency: 150,
        speedY: { min: 30, max: 80 },
        speedX: { min: -15, max: 15 },
        scale: { start: 0.25, end: 0.1 },
        alpha: { start: 0.9, end: 0.4 },
        lifespan: 5000,
        tint: THEME.colors.snowflake
      },
      default: {
        quantity: 2,
        frequency: 200,
        speedY: { min: 20, max: 50 },
        speedX: { min: -10, max: 10 },
        scale: { start: 0.2, end: 0.08 },
        alpha: { start: 0.7, end: 0.2 },
        lifespan: 6000,
        tint: THEME.colors.snowflake
      }
    };

    const weatherConfig = isStorm ? config.storm :
      isLightSnow ? config.light_snow : config.default;

    this.weatherParticles = this.scene.add.particles(0, 0, 'snow_ungroomed', {
      x: { min: 0, max: this.scene.cameras.main.width * 1.5 },
      y: -20,
      lifespan: weatherConfig.lifespan,
      speedY: weatherConfig.speedY,
      speedX: weatherConfig.speedX,
      scale: weatherConfig.scale,
      alpha: weatherConfig.alpha,
      quantity: weatherConfig.quantity,
      frequency: weatherConfig.frequency,
      tint: weatherConfig.tint,
      blendMode: 'ADD'
    });
    this.weatherParticles.setScrollFactor(0);
    this.weatherParticles.setDepth(DEPTHS.WEATHER);

    if (isStorm) {
      this.windStreaks = this.scene.add.particles(0, 0, 'snow_ungroomed', {
        x: { min: this.scene.cameras.main.width, max: this.scene.cameras.main.width + 100 },
        y: { min: 0, max: this.scene.cameras.main.height },
        lifespan: 800,
        speedX: { min: -600, max: -400 },
        speedY: { min: 50, max: 100 },
        scale: { start: 0.15, end: 0.02 },
        alpha: { start: 0.6, end: 0 },
        quantity: 2,
        frequency: 80,
        tint: THEME.colors.hailTint,
        blendMode: 'ADD'
      });
      this.windStreaks.setScrollFactor(0);
      this.windStreaks.setDepth(DEPTHS.WEATHER - 1);
    }
  }

  createNightOverlay(): void {
    // With night-colored textures, we only need a small DT for headlight glow
    this.useNightTextures = true;
    const size = HEADLIGHT_DT_SIZE;

    if (this.nightOverlay) {
      this.nightOverlay.destroy();
      this.nightOverlay = null;
    }
    if (this.scene.textures.exists(this.nightTexKey)) {
      this.scene.textures.remove(this.nightTexKey);
    }
    this.nightDynTex = this.scene.textures.addDynamicTexture(this.nightTexKey, size, size)!;
    this.nightDynTex.source[0].scaleMode = Phaser.ScaleModes.NEAREST;
    this.nightCtx = this.nightDynTex.context!;
    this.nightCtx.imageSmoothingEnabled = false;

    this.nightOverlay = this.scene.add.image(0, 0, this.nightTexKey);
    this.nightOverlay.setDepth(DEPTHS.NIGHT_OVERLAY);
    // No setScrollFactor(0) — follows world coordinates, positioned on groomer
  }

  /** Position overlay to cover full screen and map world coords to canvas pixels. */
  private prepareNightFrame(
    worldX: number, worldY: number
  ): { ctx: CanvasRenderingContext2D; w: number; h: number; zoom: number; screenX: number; screenY: number } | null {
    if (!this.nightCtx || !this.nightOverlay) return null;
    const ctx = this.nightCtx;
    const size = HEADLIGHT_DT_SIZE;
    const zoom = this.scene.cameras.main.zoom || 1;

    // Position overlay centered on the character in world space
    const displaySize = size / zoom;
    this.nightOverlay.setPosition(worldX, worldY);
    this.nightOverlay.setDisplaySize(displaySize, displaySize);

    // Clear and fill with transparency — headlight glow only
    ctx.clearRect(0, 0, size, size);

    // Center of the small DT = character position
    const screenX = size / 2;
    const screenY = size / 2;

    return { ctx, w: size, h: size, zoom, screenX, screenY };
  }

  updateNightOverlay(groomer: Phaser.Physics.Arcade.Sprite): void {
    if (!groomer) return;
    const frame = this.prepareNightFrame(groomer.x, groomer.y);
    if (!frame) return;
    const { ctx, zoom, screenX, screenY } = frame;

    this.updateFacing(groomer, 10);

    const radiusFront = this.tileSize * BALANCE.HEADLIGHT_FRONT_TILES * zoom;
    const radiusBack = this.tileSize * BALANCE.HEADLIGHT_REAR_TILES * zoom;
    const spreadAngle = BALANCE.HEADLIGHT_SPREAD;

    const groomerRadius = this.tileSize * 0.5 * zoom;
    const angle = Math.atan2(this.headlightDirection.y, this.headlightDirection.x);

    const frontX = screenX + Math.cos(angle) * groomerRadius;
    const frontY = screenY + Math.sin(angle) * groomerRadius;
    const rearX = screenX - Math.cos(angle) * groomerRadius;
    const rearY = screenY - Math.sin(angle) * groomerRadius;

    // Draw headlight glow circles (brightening on dark world)
    const steps = BALANCE.HEADLIGHT_STEPS;
    for (let i = steps - 1; i >= 0; i--) {
      const t = (i + 1) / steps;
      const stepAlpha = 0.1 * (steps - i) / steps;

      // Front work lights
      ctx.fillStyle = '#ffffee';
      this.drawLightConeCanvas(ctx, frontX, frontY, angle, radiusFront * t, spreadAngle,
        BALANCE.HEADLIGHT_DIST_STEPS, BALANCE.HEADLIGHT_ARC_STEPS, 0.4, 0.5, 6, stepAlpha);

      // Rear work lights
      ctx.fillStyle = '#ffddcc';
      this.drawLightConeCanvas(ctx, rearX, rearY, angle + Math.PI, radiusBack * t, spreadAngle * 0.9,
        BALANCE.HEADLIGHT_DIST_STEPS, BALANCE.HEADLIGHT_ARC_STEPS, 0.4, 0.5, 6, stepAlpha * 0.7);
    }

    ctx.globalAlpha = 1;
  }

  updateHeadlamp(skier: Phaser.Physics.Arcade.Sprite): void {
    if (!skier) return;
    const frame = this.prepareNightFrame(skier.x, skier.y);
    if (!frame) return;
    const { ctx, zoom, screenX, screenY } = frame;

    this.updateFacing(skier, 5);

    const radiusFront = this.tileSize * BALANCE.HEADLAMP_FRONT_TILES * zoom;
    const spreadAngle = BALANCE.HEADLAMP_SPREAD;
    const angle = Math.atan2(this.headlightDirection.y, this.headlightDirection.x);

    const headOffset = 10 * skier.scaleY * zoom;
    const forwardOffset = this.tileSize * 0.6 * zoom;
    const headX = screenX + Math.cos(angle) * forwardOffset;
    const headY = screenY - headOffset + Math.sin(angle) * forwardOffset;

    // Draw light circles on top
    ctx.fillStyle = '#ffffff';
    const steps = BALANCE.HEADLAMP_STEPS;
    for (let i = steps - 1; i >= 0; i--) {
      const t = (i + 1) / steps;
      const stepAlpha = 0.12 * (steps - i) / steps;
      this.drawLightConeCanvas(ctx, headX, headY, angle, radiusFront * t, spreadAngle,
        BALANCE.HEADLAMP_DIST_STEPS, BALANCE.HEADLAMP_ARC_STEPS, 0.2, 0.6, 3, stepAlpha);
    }

    // Ambient glow around skier
    ctx.fillStyle = '#ddeeff';
    const glowRadius = this.tileSize * 1.2 * zoom;
    for (let i = 3; i >= 0; i--) {
      const alpha = 0.04 * (4 - i) / 4;
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(screenX, screenY, glowRadius * (i + 1) / 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
  }

  private updateFacing(sprite: Phaser.Physics.Arcade.Sprite, threshold: number): void {
    const body = sprite.body as Phaser.Physics.Arcade.Body;
    if (body && (Math.abs(body.velocity.x) > threshold || Math.abs(body.velocity.y) > threshold)) {
      const len = Math.sqrt(body.velocity.x ** 2 + body.velocity.y ** 2);
      this.headlightDirection = { x: body.velocity.x / len, y: body.velocity.y / len };
    }
  }

  /** Draw light cone circles directly to canvas, erasing darkness */
  private drawLightConeCanvas(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number, angle: number, radius: number, spread: number,
    distSteps: number, arcSteps: number, sizeFactor: number, falloff: number,
    minSize: number, alpha: number
  ): void {
    ctx.globalAlpha = alpha;
    for (let d = 1; d <= distSteps; d++) {
      const dist = radius * (d / distSteps);
      const circleRadius = Math.max(radius * sizeFactor * (1 - d / distSteps * falloff), minSize);

      for (let a = -arcSteps / 2; a <= arcSteps / 2; a++) {
        const arcAngle = angle + (a / arcSteps) * spread;
        const px = cx + Math.cos(arcAngle) * dist;
        const py = cy + Math.sin(arcAngle) * dist;
        ctx.beginPath();
        ctx.arc(px, py, circleRadius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }

  /** Fixed frost texture size — small texture stretched to screen saves ~50x drawImage cost. */
  private static readonly FROST_TEX_SIZE = 128;

  createFrostOverlay(): void {
    this.rebuildFrostTexture();
  }

  /** Build the frost vignette at a fixed small resolution, stretched to screen on display. */
  private rebuildFrostTexture(): void {
    const cam = this.scene.cameras.main;
    const screenW = cam.width;
    const screenH = cam.height;

    // Only rebuild if overlay doesn't exist yet (texture is resolution-independent)
    if (this.frostOverlay) {
      this.frostOverlay.setDisplaySize(screenW, screenH);
      this.frostOverlay.setPosition(screenW / 2, screenH / 2);
      return;
    }

    const S = WeatherSystem.FROST_TEX_SIZE;
    const gfx = this.scene.make.graphics({ x: 0, y: 0 } as any, false);
    const frostColor = THEME.colors.frostOverlay;

    // Full wash — visible even at low frost levels
    gfx.fillStyle(frostColor, 0.35);
    gfx.fillRect(0, 0, S, S);

    // Thick edge bands creating a vignette from edges toward center
    const maxInset = S * 0.35;
    const steps = 8;
    for (let i = 0; i < steps; i++) {
      const t = (i + 1) / steps;
      const d = maxInset * t;
      const alpha = (1 - t * 0.6) * 0.6;
      gfx.fillStyle(frostColor, alpha);
      gfx.fillRect(0, 0, S, d);             // Top
      gfx.fillRect(0, S - d, S, d);         // Bottom
      gfx.fillRect(0, 0, d, S);             // Left
      gfx.fillRect(S - d, 0, d, S);         // Right
    }

    if (this.scene.textures.exists(this.frostTexKey)) {
      this.scene.textures.remove(this.frostTexKey);
    }
    gfx.generateTexture(this.frostTexKey, S, S);
    gfx.destroy();

    this.frostOverlay = this.scene.add.image(screenW / 2, screenH / 2, this.frostTexKey);
    this.frostOverlay.setDisplaySize(screenW, screenH);
    this.frostOverlay.setDepth(DEPTHS.FROST_OVERLAY);
    this.frostOverlay.setScrollFactor(0);
    this.frostOverlay.setAlpha(0);
  }

  updateFrostOverlay(frostLevel: number): void {
    if (!this.frostOverlay) return;
    this.frostOverlay.setAlpha(frostLevel / 100);
  }

  /** Resize frost overlay to cover new viewport. Texture stays at fixed size. */
  handleFrostResize(): void {
    if (this.frostOverlay) {
      const cam = this.scene.cameras.main;
      this.frostOverlay.setDisplaySize(cam.width, cam.height);
      this.frostOverlay.setPosition(cam.width / 2, cam.height / 2);
    }
  }

  /** Night overlay uses fixed-size DT — no resize needed. */
  handleNightResize(): void {
    // Small headlight DT is fixed size, no rebuild needed
  }

  applyAccessibilitySettings(): void {
    Accessibility.applyDOMSettings();
    this.highContrastMode = Accessibility.settings.highContrast;
  }

  reset(): void {
    this.weatherParticles = null;
    this.windStreaks = null;
    this.nightOverlay = null;
    this.nightCtx = null;
    this.nightDynTex = null;
    this.frostOverlay = null;
    this.headlightDirection = { x: 0, y: -1 };
  }
}
